<template>
  <Teleport to="body">
    <div
      v-if="isModalOpen"
      :class="[containerClass, $style.container, 'modal', modalActive && 'is-active']"
    >
      <div :class="['modal-background', $style.background]" @click="internallyCloseModal" />
      <div :class="['modal-content', $style.contentContainer, isProduct && $style.product]">
        <div
          ref="modalContent"
          :class="[
            $style.content,
            !shouldShowConsent && isVideo && $style.video,
            !shouldShowConsent && isVideo && isNotYouTube && $style.notYouTube,
          ]"
          @click.stop
        >
          <slot v-if="!shouldShowConsent" />
          <div v-else :class="[$style.consent]">
            <h2 :class="$style.consentHeading">
              {{ storeTranslations.translations?.['consentHeading'] || fallbackConsentHeading }}
            </h2>
            <div :class="$style.consentContent" v-html="vendorConsentText" />
            <Button1XP
              :class="$style.consentButton"
              :text="consentButtonLabel"
              @click="handleContentButton"
            />
          </div>
        </div>
      </div>
      <Button1XP
        aria-label="close"
        :class="$style.close"
        size="s"
        start-icon="close"
        @click="internallyCloseModal"
      />
    </div>
  </Teleport>
</template>

<script setup>
import { onMounted, onUnmounted, onUpdated, ref, watch, nextTick, computed } from 'vue';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import { getMarketingConsent } from '../../globals/cookiebot';
import Button1XP from '../Button1XP/Button1XP.vue';
import { useStoreTranslations } from '../../store/translations/translations';

const emit = defineEmits(['closeModal', 'openModal', 'consentUpdated', 'startChat']);

const props = defineProps({
  containerClass: {
    type: String,
    default: undefined,
  },
  isModalOpen: {
    type: Boolean,
    default: false,
  },
  isVideo: {
    type: Boolean,
    default: false,
  },
  isNotYouTube: {
    type: Boolean,
    default: false,
  },
  isProduct: {
    type: Boolean,
    default: false,
  },
  isChat: {
    type: Boolean,
    default: false,
  },
  vendorKey: {
    type: String,
    default: undefined,
  },
  vendorId: {
    type: String,
    default: undefined,
  },
});

const storeTranslations = useStoreTranslations();
const dictionary = window.dictionary;

const vendor = computed(() => {
  if (props.vendorKey && cmpVendorIDs[props.vendorKey]) {
    return cmpVendorIDs[props.vendorKey];
  }

  if (props.isChat) {
    return cmpVendorIDs.MICROSOFT_LIVE_CHAT;
  }

  return cmpVendorIDs.YOUTUBE;
});
const currentVendorId = computed(() => props.vendorId || vendor.value.id);
const modalActive = ref(false);
const htmlElement = ref(null);
const consentGiven = ref(
  window.Cookiebot ? getMarketingConsent() : getVendorConsent(currentVendorId.value),
);
const modalContent = ref(null);

const vendorConsentText = computed(
  () =>
    storeTranslations.translations?.['consentText']
      ?.replaceAll('###VENDOR_NAME###', vendor.value.name)
      ?.replaceAll('#<VENDOR_NAME>#', vendor.value.name) || fallbackConsentText,
);
const consentButtonLabel = computed(
  () =>
    storeTranslations.translations?.['consentButtonLabel']?.replaceAll(/&apos;/g, "'") ||
    fallbackConsentButtonLabel,
);

const fallbackConsentHeading = computed(() => dictionary?.consentHeading);
const fallbackConsentText = dictionary?.consentText
  ?.replaceAll('###VENDOR_NAME###', vendor.value.name)
  .replaceAll('#<VENDOR_NAME>#', vendor.value.name);
const fallbackConsentButtonLabel = dictionary?.consentButtonLabel?.replaceAll(/&apos;/g, "'");

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(currentVendorId.value);
  checkConsent();
};

const handleContentButton = () => {
  const vendorId = props.vendorId || currentVendorId.value;

  if (window.cmpConsentType === 'consentmanager') {
    window.__cmp &&
      window.__cmp('setVendorConsent', [vendorId, 1], () => {
        consentGiven.value = getVendorConsent(vendorId);
        checkConsent();
        if (consentGiven.value) {
          emit('consentUpdated', consentGiven.value);
          emit('startChat');
          if (props.isChat) {
            closeModal();
          }
        }
      });
  } else {
    if (window.Cookiebot) {
      window.Cookiebot.onaccept = () => {
        consentGiven.value = getMarketingConsent();
        if (consentGiven.value) {
          emit('consentUpdated', consentGiven.value);
        }
      };
      window.Cookiebot.show();
    }
    checkConsent();
  }
};

const shouldShowConsent = computed(() => {
  if (props.vendorKey || props.vendorId) {
    return !consentGiven.value;
  }

  return (
    (props.isVideo && !props.isNotYouTube && !consentGiven.value) ||
    (props.isChat && !consentGiven.value)
  );
});

const checkConsent = () => {
  nextTick(() => {
    const iframe = modalContent.value?.querySelector('iframe');
    if (iframe && props.isVideo) {
      if (consentGiven.value && modalActive.value) {
        if (!iframe.src) {
          iframe.src = iframe.getAttribute('data-src');
          iframe.removeAttribute('data-src');
        }
      } else {
        if (iframe.src) {
          iframe.setAttribute('data-src', iframe.src);
          iframe.removeAttribute('src');
        }
      }
    }
  });
};

const openModal = () => {
  if (props.isChat && consentGiven.value) {
    return;
  }
  modalActive.value = true;
  htmlElement.value.classList.add('is-clipped');
  emit('openModal');
};

const closeModal = () => {
  modalActive.value = false;
  htmlElement.value.classList.remove('is-clipped');
  if (props.isVideo && !props.isNotYouTube) {
    modalContent.value
      .querySelector('iframe')
      ?.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
  }
};

const internallyCloseModal = () => {
  closeModal();

  window.dispatchEvent(
    new CustomEvent('modal:close', {
      detail: { props },
    }),
  );

  emit('closeModal');
};

const escCloseModal = (event) => {
  if (event.keyCode === 27) {
    internallyCloseModal();
  }
};

watch(
  () => props.isModalOpen,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      newValue === true ? openModal() : closeModal();
    }
  },
);

watch(modalActive, () => {
  consentGiven.value =
    window.cmpConsentType === 'cookiebot'
      ? getMarketingConsent()
      : getVendorConsent(currentVendorId.value);
  checkConsent();
});

onMounted(() => {
  registerConsentListeners(checkVendorConsent);
  window.addEventListener('keyup', escCloseModal);
  htmlElement.value = document.querySelector('html');
});

onUpdated(() => {
  if (window.Cookiebot) {
    consentGiven.value = getMarketingConsent();
  } else {
    consentGiven.value = getVendorConsent(currentVendorId.value);
  }
});

onUnmounted(() => {
  window.removeEventListener('keyup', escCloseModal);
});
</script>

<style module lang="scss">
@forward './Modal.scss';
</style>
